import type { BrowserSession } from "../api/accountsApi";
import { decisionOn, unlisted, type AccessRefusal, type Decision } from "../api/access/decisions";
import type { PermittedAction } from "../api/permittedActions";
import type { UiError } from "../errors/uiError";
import { defineStateMachine, type TransitionTable } from "../shared/state/stateMachine";

/**
 * Who is using the web app: the browser's cookie session (`docs/components/accounts.md`).
 *
 * `expired` is a session that ended while the app was open (its lifetime ran out, or it
 * was ended elsewhere): the server answered `authentication_required`. The app then asks
 * the user to sign in again instead of failing pane by pane.
 */
export type SessionState =
  | { readonly status: "checking" }
  | { readonly status: "checkFailed"; readonly error: UiError }
  | { readonly status: "signedOut" }
  | { readonly status: "signedIn"; readonly session: BrowserSession }
  | { readonly status: "expired" };

export type SessionEvent =
  | { readonly type: "sessionFound"; readonly session: BrowserSession }
  | { readonly type: "noSessionFound" }
  | { readonly type: "sessionCheckFailed"; readonly error: UiError }
  | { readonly type: "retryRequested" }
  | { readonly type: "signedIn"; readonly session: BrowserSession }
  | { readonly type: "signedOut" }
  | { readonly type: "sessionEnded" };

export const INITIAL_SESSION_STATE: SessionState = { status: "checking" };

/**
 * `sessionEnded` comes from any request answered `authentication_required`. Only a signed-in
 * session can end; in every other state it is a late or expected answer (the session check
 * itself, a request still running after sign-out) and changes nothing.
 */
const keepState = <S extends SessionState>(state: S): S => state;

const SESSION_TRANSITIONS: TransitionTable<SessionState, SessionEvent> = {
  checking: {
    sessionFound: (_state, event) => ({ status: "signedIn", session: event.session }),
    noSessionFound: () => ({ status: "signedOut" }),
    sessionCheckFailed: (_state, event) => ({ status: "checkFailed", error: event.error }),
    // A sign-in that completes first wins; the provider then drops the check's answer.
    signedIn: (_state, event) => ({ status: "signedIn", session: event.session }),
    sessionEnded: keepState,
  },
  checkFailed: {
    retryRequested: () => ({ status: "checking" }),
    sessionEnded: keepState,
  },
  signedOut: {
    signedIn: (_state, event) => ({ status: "signedIn", session: event.session }),
    sessionEnded: keepState,
  },
  signedIn: {
    signedOut: () => ({ status: "signedOut" }),
    sessionEnded: () => ({ status: "expired" }),
  },
  expired: {
    signedIn: (_state, event) => ({ status: "signedIn", session: event.session }),
    signedOut: () => ({ status: "signedOut" }),
    sessionEnded: keepState,
  },
};

export const transitionSession = defineStateMachine<SessionState, SessionEvent>("session", SESSION_TRANSITIONS);

/** Whether the app's working sections may open in this state. */
export function sessionAllowsAppAccess(state: SessionState): state is Extract<SessionState, { status: "signedIn" }> {
  return state.status === "signedIn";
}

/** The CSRF token unsafe requests must carry in this state, if any. */
export function csrfTokenOf(state: SessionState): string | null {
  return state.status === "signedIn" ? state.session.csrfToken : null;
}

/** The signed-in account's decision on the installation action `action` (SOT-02); signed out, nothing is offered. */
export function sessionDecision(state: SessionState, action: PermittedAction): Decision<AccessRefusal> {
  return state.status === "signedIn" ? decisionOn(state.session.decisions, action, null) : unlisted(null);
}
